import * as React from 'react';
import {
  Autocomplete,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import { ReactComponent as ArrowRight } from 'assets/images/arrow_right_1.svg';
import { EUserRole } from 'enums';

import { useInviteUsersFormManager } from './invite-users-form.hook';

interface InviteUsersFormProps {
  onLoading?: (loading: boolean) => void;
  next?: (data?: any) => void;
}

export const InviteUsersForm = React.forwardRef(({ onLoading = () => {}, next }: InviteUsersFormProps, ref) => {
  const theme = useTheme();
  const [emailsInputValue, setEmailsInputValue] = React.useState('');

  const [formManager, { isLoading }] = useInviteUsersFormManager({ next });

  React.useImperativeHandle(ref, () => ({ onSubmit: () => formManager.handleSubmit() }), []);

  React.useEffect(() => onLoading(isLoading), [isLoading]);

  return (
    <form onSubmit={formManager.handleSubmit}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            freeSolo
            value={formManager.values.emails}
            inputValue={emailsInputValue}
            options={[]}
            onChange={(_, emails) => {
              formManager.setFieldValue('emails', emails);
            }}
            onInputChange={(event: any, newInputValue: string) => {
              const options = newInputValue.split(',');

              if (options.length > 1) {
                formManager.setFieldValue(
                  'emails',
                  (formManager.values.emails || [])
                    .concat(options)
                    .map((x) => x.trim())
                    .filter((x) => x),
                );
                setEmailsInputValue('');
              } else {
                setEmailsInputValue(newInputValue);
              }
            }}
            onBlur={(event: any) => {
              const option = event.target?.value.trim();

              if (option) {
                formManager.setFieldValue('emails', [...(formManager.values.emails || []), option]);
                setEmailsInputValue('');
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Emails"
                placeholder="Add emails sperated by commas."
                error={!!(formManager.errors.emails && formManager.touched.emails)}
                helperText={
                  formManager.touched.emails && formManager.errors.emails
                    ? formManager.errors.emails
                    : 'Add up to 10 emails by typing the email address.'
                }
              />
            )}
            sx={{
              marginTop: 2,
            }}
            autoHighlight
          />
        </Grid>

        <Grid item xs={12}>
          <Typography
            variant="body2"
            sx={{
              marginBottom: 1,
            }}
          >
            Select User Role
          </Typography>
          <FormControl>
            <RadioGroup
              defaultValue={EUserRole.BASIC}
              onChange={(e) => formManager.setFieldValue('role', e.target.value)}
            >
              <FormControlLabel
                control={<Radio value={EUserRole.BASIC} />}
                label={
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                    Basic
                  </Typography>
                }
              />
              <Typography variant="body2" color="textSecondary" sx={{ marginLeft: '32px' }}>
                User account with access to view data and make non-admin changes.
              </Typography>
              <FormControlLabel
                sx={{
                  paddingTop: '16px',
                }}
                control={<Radio value={EUserRole.ADMIN} />}
                label={
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                    Admin
                  </Typography>
                }
              />
              <Typography variant="body2" color="textSecondary" sx={{ marginLeft: '32px' }}>
                User account with full access to modify organization and billing settings.
              </Typography>
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            multiline
            fullWidth
            rows={4}
            value={formManager.values.message}
            variant="outlined"
            label="Message"
            placeholder="Add a custom message"
            onChange={(e) => formManager.setFieldValue('message', e.target.value)}
            error={!!formManager.errors.message}
            helperText={
              !!formManager.errors.message ? formManager.errors.message : `${formManager.values.message.length} / 1000`
            }
          />
        </Grid>

        <Grid item hidden xs={12}>
          <Button
            color="primary"
            endIcon={<ArrowRight />}
            disabled={formManager.isSubmitting}
            type="submit"
            sx={{
              height: 40,
              paddingX: 4,
              backgroundColor: '#FF8900',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#FF8900 !important',
              },
            }}
          >
            <Typography>Invite Users</Typography>
          </Button>
        </Grid>
      </Grid>
    </form>
  );
});
